import React, { useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@ui/Icon';
import { isAdmin } from '@shared/helpers/user.helpers';
import { useUser } from '@client/hooks/use-user.hooks';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import { Coordinate } from '@shared/models/geography.model';
import { type MapRenderAiProvider } from '@shared/models/design/map-render.model';
import {
  isAllowedGeminiImageModel,
  isAllowedGrokImageModel,
  isAllowedOpenAiImageModel,
} from '@shared/models/ai/ai-models.model';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { buildClientControlledSystemPrompt } from '../helpers/design-ai-render-prompt.helpers';
import {
  buildGenerateImageAiForProvider,
  buildGenerateImageDialogDefaults,
  generateImageModelsForProvider,
  type GenerateImageDialogDefaults,
  type GenerateImageDialogValue,
  type GenerateImageSourceType,
  canSubmitGenerateImageDialog,
} from '../helpers/generate-image-dialog.helpers';
import type { ViewMode } from './ViewModeToggle';

export {
  buildGenerateImageDialogDefaults,
  type GenerateImageDialogDefaults,
  type GenerateImageDialogValue,
  type GenerateImageSourceType,
};

type GenerateImageDialogProps = {
  open: boolean;
  defaults: GenerateImageDialogDefaults;
  onClose: (value: GenerateImageDialogValue | undefined) => void;
  mode: ViewMode;
  location: Coordinate | undefined;
  buildingDesign: BuildingDesign;
};

type AiSelection = { provider: MapRenderAiProvider; model: string };

/**
 * Shared Generate-image dialog for map capture and the report cover Admin Area action.
 * Description is look/mood; Advanced (admins) can edit the system prompt.
 * The server still prepends safety restrictions.
 */
export const GenerateImageDialog: React.FC<GenerateImageDialogProps> = ({
  open,
  defaults,
  onClose,
  mode,
  location,
  buildingDesign,
}) => {
  const { classes } = useStyles();
  const user = useUser();
  // Hide Advanced from everyone except admins, including after Generate image
  // is opened to regular users, so we can keep experimenting with prompts,
  // models, and geometry format.
  const canEditAdvanced = isAdmin(user);
  const initialAi: AiSelection = {
    provider: defaults.provider,
    model: defaults.model,
  };

  const [ai, setAi] = useState<AiSelection>(initialAi);
  const [sourceType, setSourceType] = useState<GenerateImageSourceType>(
    defaults.sourceType,
  );
  const [systemPrompt, setSystemPrompt] = useState<string>(
    defaults.systemPrompt,
  );
  const [defaultSystemPrompt, setDefaultSystemPrompt] = useState<string>(
    defaults.systemPrompt,
  );
  const [userPrompt, setUserPrompt] = useState<string>(defaults.userPrompt);
  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false);

  const provider = ai.provider;
  const model = ai.model;

  const models = generateImageModelsForProvider(provider);

  const handleProviderChange = (nextProvider: MapRenderAiProvider) => {
    setAi(buildGenerateImageAiForProvider(nextProvider, defaults));
  };

  const handleModelChange = (nextModel: unknown) => {
    switch (provider) {
      case 'openai':
        if (!isAllowedOpenAiImageModel(nextModel)) return;
        setAi({ provider: 'openai', model: nextModel });
        return;
      case 'gemini':
        if (!isAllowedGeminiImageModel(nextModel)) return;
        setAi({ provider: 'gemini', model: nextModel });
        return;
      case 'grok':
        if (!isAllowedGrokImageModel(nextModel)) return;
        setAi({ provider: 'grok', model: nextModel });
        return;
      default:
        assertUnreachable(provider);
    }
  };

  const handleInputTypeChange = (nextSourceType: GenerateImageSourceType) => {
    setSourceType(nextSourceType);
    const nextSystemPrompt = buildClientControlledSystemPrompt({
      sourceType: nextSourceType,
      mode,
      camera: defaults.camera,
      location,
      buildingDesign,
    });

    // Keep an untouched system prompt in sync with geometry format; never
    // discard edits the admin has already made. Description is look/mood only
    // and does not depend on geometry format.
    if (systemPrompt.trim() === defaultSystemPrompt.trim()) {
      setSystemPrompt(nextSystemPrompt);
    }
    setDefaultSystemPrompt(nextSystemPrompt);
  };

  const canSubmit = canSubmitGenerateImageDialog({
    userPrompt,
    systemPrompt,
    canEditAdvanced,
  });

  const handleCancel = () => onClose(undefined);

  const handleCreate = () => {
    if (!canSubmit) return;
    const value: GenerateImageDialogValue = {
      ...ai,
      sourceType,
      systemPrompt: systemPrompt.trim(),
      userPrompt: userPrompt.trim(),
      model,
    };
    onClose(value);
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Generate image</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <TextField
          className={classes.dialogField}
          label="Description"
          value={userPrompt}
          onChange={(e) => setUserPrompt(e.target.value)}
          multiline
          minRows={4}
          fullWidth
        />

        {canEditAdvanced && (
          <div className={classes.advancedSection}>
            <Button
              className={classes.advancedToggle}
              onClick={() => setIsAdvancedExpanded((expanded) => !expanded)}
              aria-expanded={isAdvancedExpanded}
              startIcon={
                <Icon
                  category="mui"
                  icon={
                    isAdvancedExpanded
                      ? 'KeyboardArrowDown'
                      : 'KeyboardArrowRight'
                  }
                />
              }
            >
              Advanced
            </Button>
            <Collapse in={isAdvancedExpanded} unmountOnExit>
              <div className={classes.advancedContent}>
                <Typography className={classes.dialogHint} variant="body2">
                  {
                    'Optional {{user-prompt}} and {{json}} are substituted when present; omit them to leave those parts out. The server still prepends non-negotiable restrictions. A screenshot, when used, is attached as an image — not as text.'
                  }
                </Typography>
                <TextField
                  className={classes.dialogField}
                  label="System prompt"
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  multiline
                  minRows={5}
                  fullWidth
                />

                <div className={classes.dialogRow}>
                  <FormControl className={classes.dialogField} fullWidth>
                    <InputLabel id="generate-image-provider-label">
                      Provider
                    </InputLabel>
                    <Select
                      labelId="generate-image-provider-label"
                      label="Provider"
                      value={provider}
                      onChange={(e) => {
                        const next = parseProvider(e.target.value);
                        if (!next) return;
                        handleProviderChange(next);
                      }}
                    >
                      <MenuItem value="openai">Open AI</MenuItem>
                      <MenuItem value="gemini">Google Gemini</MenuItem>
                      <MenuItem value="grok">xAI Grok</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl className={classes.dialogField} fullWidth>
                    <InputLabel id="generate-image-model-label">
                      Model
                    </InputLabel>
                    <Select
                      labelId="generate-image-model-label"
                      label="Model"
                      value={model}
                      onChange={(e) => handleModelChange(e.target.value)}
                    >
                      {models.map((allowedModel) => (
                        <MenuItem key={allowedModel} value={allowedModel}>
                          {allowedModel}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>

                <FormControl className={classes.dialogField} fullWidth>
                  <InputLabel id="generate-image-geometry-label">
                    Geometry format
                  </InputLabel>
                  <Select
                    labelId="generate-image-geometry-label"
                    label="Geometry format"
                    value={sourceType}
                    onChange={(e) => {
                      const next = parseSourceType(e.target.value);
                      if (!next) return;
                      handleInputTypeChange(next);
                    }}
                  >
                    <MenuItem value="building-json">json</MenuItem>
                    <MenuItem value="screenshot">image</MenuItem>
                    <MenuItem value="screenshot+building-json">
                      image + json
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>
            </Collapse>
          </div>
        )}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!canSubmit}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const parseProvider = (value: unknown): MapRenderAiProvider | undefined => {
  if (value === 'openai' || value === 'gemini' || value === 'grok') {
    return value;
  }
  return undefined;
};

const parseSourceType = (
  value: unknown,
): GenerateImageSourceType | undefined => {
  if (
    value === 'building-json' ||
    value === 'screenshot' ||
    value === 'screenshot+building-json'
  ) {
    return value;
  }
  return undefined;
};

const useStyles = makeStyles()((theme) => ({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(1),
  },
  dialogRow: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  dialogField: {
    width: '100%',
  },
  dialogActions: {
    padding: theme.spacing(1.5, 3, 2),
  },
  dialogHint: {
    color: theme.palette.text.secondary,
  },
  advancedSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  advancedToggle: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  advancedContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));
